import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { SafeText } from "@/shared/components/SafeText";
import type { AssignmentResultRow } from "./access-client";
import {
  ManageLocationsFooter,
  ManageLocationsForm,
  ManageLocationsResults,
} from "./ManageLocationsSections";
import { useManageLocationsState } from "./use-manage-locations-state";

interface ManageMemberLocationsDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly organizationId: string;
  readonly membershipId: string;
  /** The member's account id: the full assignment list is read from the user's memberships. */
  readonly userId: string;
  readonly memberName: string;
  /** Fired once the API applied the change; the dialog stays open on the result list. */
  readonly onDone: (results: readonly AssignmentResultRow[]) => void;
  /**
   * The member stopped being an active Staff (or vanished) meanwhile. The reads
   * were refreshed; the dialog closes and hands the reason over.
   */
  readonly onStale: (message: string) => void;
}

/**
 * Add and remove the locations of ONE active Staff member of an organization
 * in a single all-or-nothing call, then show what the system did with every
 * location. Both lists are limited to the organization's own locations.
 */
export function ManageMemberLocationsDialog(props: ManageMemberLocationsDialogProps) {
  // Mounted only while open (and per membership): selections and errors start clean.
  if (!props.open) return null;
  return <ManageMemberLocationsDialogBody key={props.membershipId} {...props} />;
}

function ManageMemberLocationsDialogBody({
  open,
  onOpenChange,
  organizationId,
  membershipId,
  userId,
  memberName,
  onDone,
  onStale,
}: ManageMemberLocationsDialogProps) {
  const { t } = useTranslation();
  const state = useManageLocationsState({
    organizationId,
    membershipId,
    userId,
    onDone,
    onStale,
    onOpenChange,
  });

  return (
    <Dialog open={open} onOpenChange={state.handleOpenChange}>
      <DialogContent
        data-testid="manage-locations-dialog"
        className="max-h-[90vh] max-w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle>{t("organizationAccess.manageLocations.title")}</DialogTitle>
          <DialogDescription>
            <SafeText>{memberName}</SafeText>
            {` · ${t("organizationAccess.roles.STAFF")}`}
          </DialogDescription>
        </DialogHeader>
        {state.results ? (
          <ManageLocationsResults results={state.results} names={state.names} />
        ) : (
          <ManageLocationsForm state={state} organizationId={organizationId} />
        )}
        <DialogFooter>
          <ManageLocationsFooter state={state} onClose={() => onOpenChange(false)} />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
